import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  MessageSquare,
  ListTodo,
  Settings as SettingsIcon,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  Bell,
  User,
  Users,
  Shield,
  Palette,
  Volume2,
  HelpCircle,
  Activity,
  Mic,
  AlertOctagon,
  Info,
  Sparkles,
  Zap,
  Lock,
  History,
  ChevronRight,
} from 'lucide-react'
import { ChatContainer } from '@/components/chat/ChatContainer'
import ConversationSidebar from '@/components/ConversationSidebar'
import { TaskList } from '@/components/tasks/TaskList'
import { ContactsPage } from '@/components/contacts'
import { ScheduledTasksPage } from '@/components/scheduler'
import { NikiAvatar } from '@/components/niki/NikiAvatar'
import { KillSwitchConfirmDialog } from '@/components/shared/KillSwitchConfirmDialog'
import { AppearanceModal, ProfileModal, NotificationsModal, SecurityModal, VoiceModal, PINModal } from '@/components/settings'
import { useAuth } from '@/hooks/useAuth'
import { useTasks } from '@/hooks/useTasks'
import { useUIStore } from '@/store/uiStore'
import { useConversationStore } from '@/store/conversationStore'
import { useChatStore } from '@/store/chatStore'
import { useKillSwitch } from '@/hooks/useKillSwitch'
import { cn } from '@/utils'

type View = 'chat' | 'conversations' | 'tasks' | 'abilities' | 'settings' | 'contacts'

export default function Home() {
  const { user, logout } = useAuth()
  const { loadTasks, runningTasks } = useTasks()
  const { sidebarOpen, setSidebarOpen, tasksPanelOpen, toggleTasksPanel } = useUIStore()
  const { isTyping } = useChatStore()
  const { hasActiveTasks, openConfirm } = useKillSwitch()
  const [activeView, setActiveView] = useState<View>('chat')
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [settingsToast, setSettingsToast] = useState<string | null>(null)

  // Load tasks on mount
  useEffect(() => {
    loadTasks()
  }, [loadTasks])

  // Determine avatar state based on context
  const getAvatarState = () => {
    if (isTyping) return 'thinking'
    return 'idle'
  }

  const showToast = (message: string) => {
    setSettingsToast(message)
    setTimeout(() => setSettingsToast(null), 3000)
  }

  const navItems: { id: View; label: string; icon: typeof MessageSquare; badge?: number }[] = [
    { id: 'chat', label: 'Chat', icon: MessageSquare },
    { id: 'conversations', label: 'History', icon: History },
    { id: 'tasks', label: 'Tasks', icon: ListTodo, badge: runningTasks.length || undefined },
    { id: 'contacts', label: 'Contacts', icon: Users },
    { id: 'settings', label: 'Settings', icon: SettingsIcon },
  ]

  return (
    <div className="min-h-screen flex relative overflow-hidden">
      {/* Animated background */}
      <div className="fixed inset-0 bg-mesh opacity-30 pointer-events-none" />

      {/* Floating decorative orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute w-96 h-96 rounded-full bg-emerald-500/5 blur-3xl"
          animate={{ x: [0, 30, 0], y: [0, -20, 0] }}
          transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut' }}
          style={{ top: '5%', right: '5%' }}
        />
        <motion.div
          className="absolute w-80 h-80 rounded-full bg-emerald-600/5 blur-3xl"
          animate={{ x: [0, -20, 0], y: [0, 30, 0] }}
          transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
          style={{ bottom: '10%', left: '10%' }}
        />
      </div>

      {/* Desktop Sidebar */}
      <motion.aside
        initial={false}
        animate={{ width: sidebarOpen ? 280 : 88 }}
        className={cn(
          'hidden lg:flex fixed left-0 top-0 bottom-0 z-30',
          'flex-col glass border-r border-white/10'
        )}
      >
        {/* Header */}
        <div className="p-5 flex items-center gap-4">
          <NikiAvatar size="md" state={getAvatarState()} showRipples={false} />
          <AnimatePresence>
            {sidebarOpen && (
              <motion.div
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: 'auto' }}
                exit={{ opacity: 0, width: 0 }}
                className="overflow-hidden"
              >
                <h1 className="text-2xl font-bold text-gradient-primary tracking-wider">
                  NIKI
                </h1>
                <p className="text-xs text-white/50">AI Assistant</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Toggle button */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="absolute -right-3 top-20 w-6 h-6 rounded-full glass flex items-center justify-center text-white/60 hover:text-white transition-colors"
        >
          <ChevronLeft className={cn('w-4 h-4 transition-transform', !sidebarOpen && 'rotate-180')} />
        </button>

        {/* Navigation */}
        <nav className="flex-1 py-4 space-y-1">
          {navItems.map((item) => (
            <NavItem
              key={item.id}
              icon={item.icon}
              label={item.label}
              isActive={activeView === item.id}
              collapsed={!sidebarOpen}
              badge={item.badge}
              onClick={() => setActiveView(item.id)}
            />
          ))}

          {/* Quick Abilities Section (shows after chatting) */}
          <SidebarAbilities collapsed={!sidebarOpen} onAbilityClick={() => setActiveView('abilities')} />
        </nav>

        {/* Kill Switch - Always visible, disabled when no tasks */}
        <div className={cn('px-4 py-3', !sidebarOpen && 'flex justify-center')}>
          <motion.button
            onClick={hasActiveTasks ? openConfirm : undefined}
            disabled={!hasActiveTasks}
            className={cn(
              'flex items-center gap-2 p-2 rounded-lg transition-all',
              hasActiveTasks
                ? 'bg-danger/20 text-danger hover:bg-danger/30 kill-switch-pulse'
                : 'bg-white/5 text-white/30 cursor-not-allowed',
              sidebarOpen ? 'w-full' : 'w-10 h-10 justify-center'
            )}
            whileHover={hasActiveTasks ? { scale: 1.02 } : undefined}
            whileTap={hasActiveTasks ? { scale: 0.98 } : undefined}
          >
            <AlertOctagon className="w-5 h-5" />
            {sidebarOpen && <span className="text-sm font-medium">Stop All Tasks</span>}
          </motion.button>
        </div>

        {/* User Profile */}
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold shrink-0"
              style={{
                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              }}
            >
              {user?.name?.charAt(0).toUpperCase() || 'U'}
            </div>
            <AnimatePresence>
              {sidebarOpen && (
                <motion.div
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 0 }}
                  className="overflow-hidden flex-1 min-w-0"
                >
                  <p className="text-sm font-medium text-white truncate">{user?.name || 'User'}</p>
                  <p className="text-xs text-white/50 truncate">{user?.email}</p>
                </motion.div>
              )}
            </AnimatePresence>
            <button
              onClick={logout}
              className={cn(
                'p-2 rounded-lg text-white/50 hover:text-danger hover:bg-danger/10 transition-all',
                !sidebarOpen && 'mx-auto mt-2'
              )}
              title="Sign out"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </motion.aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-30 glass border-b border-white/10">
        <div className="flex items-center justify-between px-4 py-3">
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="p-2 text-white/70 hover:text-white"
          >
            <Menu className="w-6 h-6" />
          </button>

          <div className="flex items-center gap-2">
            <NikiAvatar size="sm" state={getAvatarState()} showRipples={false} />
            <span className="text-lg font-bold text-gradient-primary">NIKI</span>
          </div>

          {/* Kill switch always visible, disabled when no tasks */}
          <button
            onClick={hasActiveTasks ? openConfirm : undefined}
            disabled={!hasActiveTasks}
            className={cn(
              'p-2 rounded-lg transition-all',
              hasActiveTasks
                ? 'bg-danger/20 text-danger kill-switch-pulse'
                : 'bg-white/5 text-white/30'
            )}
          >
            <AlertOctagon className="w-5 h-5" />
          </button>
        </div>

        {/* Mobile Tab Bar */}
        <div className="flex border-t border-white/10">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveView(item.id)}
              className={cn(
                'flex-1 py-3 flex flex-col items-center gap-1 transition-colors',
                activeView === item.id ? 'text-emerald-400' : 'text-white/50'
              )}
            >
              <div className="relative">
                <item.icon className="w-5 h-5" />
                {item.badge && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full text-[10px] font-bold flex items-center justify-center text-white">
                    {item.badge}
                  </span>
                )}
              </div>
              <span className="text-xs">{item.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 bg-black/60 z-40"
              onClick={() => setMobileMenuOpen(false)}
            />
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25 }}
              className="lg:hidden fixed left-0 top-0 bottom-0 w-72 z-50 glass border-r border-white/10 flex flex-col"
            >
              <div className="p-5 flex items-center justify-between border-b border-white/10">
                <div className="flex items-center gap-3">
                  <NikiAvatar size="md" state={getAvatarState()} showRipples={false} />
                  <div>
                    <h1 className="text-xl font-bold text-gradient-primary">NIKI</h1>
                    <p className="text-xs text-white/50">AI Assistant</p>
                  </div>
                </div>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2 text-white/50 hover:text-white"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <nav className="flex-1 py-4 space-y-1">
                {navItems.map((item) => (
                  <NavItem
                    key={item.id}
                    icon={item.icon}
                    label={item.label}
                    isActive={activeView === item.id}
                    collapsed={false}
                    badge={item.badge}
                    onClick={() => {
                      setActiveView(item.id)
                      setMobileMenuOpen(false)
                    }}
                  />
                ))}
              </nav>

              <div className="p-4 border-t border-white/10">
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold"
                    style={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' }}
                  >
                    {user?.name?.charAt(0).toUpperCase() || 'U'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                    <p className="text-xs text-white/50 truncate">{user?.email}</p>
                  </div>
                  <button onClick={logout} className="p-2 text-white/50 hover:text-danger">
                    <LogOut className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main
        className={cn(
          'flex-1 transition-all duration-300 relative z-10',
          'lg:ml-[88px]',
          sidebarOpen && 'lg:ml-[280px]',
          'pt-[120px] lg:pt-0' // Account for mobile header
        )}
      >
        <AnimatePresence mode="wait">
                    {activeView === 'chat' && (
            <motion.div
              key="chat"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="h-screen flex"
            >
              {/* Chat Section */}
              <div className="flex-1 flex flex-col">
                <ChatContainer />
              </div>

              {/* Tasks Sidebar (desktop) - Collapsible */}
              <motion.div 
                className="hidden xl:flex flex-col border-l border-white/10 glass-light"
                animate={{ width: tasksPanelOpen ? 380 : 56 }}
                transition={{ duration: 0.3 }}
              >
                {/* Toggle button */}
                <button
                  onClick={toggleTasksPanel}
                  className="p-4 flex items-center justify-center hover:bg-white/5 transition-colors border-b border-white/10"
                >
                  {tasksPanelOpen ? (
                    <ChevronRight className="w-5 h-5 text-white/70" />
                  ) : (
                    <div className="relative">
                      <Activity className="w-5 h-5 text-emerald-400" />
                      {runningTasks.length > 0 && (
                        <span className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full text-[10px] font-bold flex items-center justify-center text-white">
                          {runningTasks.length}
                        </span>
                      )}
                    </div>
                  )}
                </button>

                {tasksPanelOpen ? (
                  <div className="flex-1 overflow-y-auto p-6">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-xl font-semibold text-white">Active Tasks</h2>
                      {runningTasks.length > 0 && (
                        <span className="px-2 py-1 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-medium">
                          {runningTasks.length} running
                        </span>
                      )}
                    </div>

                    <div className="mb-4 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-start gap-2">
                      <Info className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                      <p className="text-xs text-white/70">
                        When you ask Niki to perform actions, tasks appear here.
                      </p>
                    </div>

                    <TaskList />
                  </div>
                ) : (
                  <div className="flex-1 flex flex-col items-center py-4 gap-2">
                    {runningTasks.slice(0, 5).map((_task, i) => (
                      <div key={i} className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                        <Activity className="w-4 h-4 text-emerald-400 animate-pulse" />
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            </motion.div>
          )}

          {activeView === 'conversations' && (
            <motion.div
              key="conversations"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="h-screen"
            >
              <div className="h-full max-w-2xl mx-auto">
                <ConversationSidebar
                  onNewChat={() => {
                    setActiveView('chat')
                  }}
                  onSelectConversation={(id) => {
                    useConversationStore.getState().setCurrentConversation(id)
                    setActiveView('chat')
                  }}
                  selectedConversationId={useConversationStore.getState().currentConversation?.id}
                />
              </div>
            </motion.div>
          )}

          {activeView === 'tasks' && (
            <motion.div
              key="tasks"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="h-screen overflow-y-auto p-6"
            >
              <TasksPageContent 
                runningTasks={runningTasks}
                
              />
            </motion.div>
          )}

          {activeView === 'abilities' && (
            <motion.div
              key="abilities"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="h-screen overflow-y-auto p-6"
            >
              <AbilitiesPage onTryAbility={() => {
                setActiveView('chat')
              }} />
            </motion.div>
          )}

          {activeView === 'settings' && (
            <motion.div
              key="settings"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="h-screen overflow-y-auto p-6"
            >
              <SettingsPage user={user} onItemClick={showToast} />
            </motion.div>
          )}

          {activeView === 'contacts' && (
            <motion.div
              key="contacts"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="h-screen overflow-y-auto p-6"
            >
              <ContactsPage />
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Settings Toast */}
      <AnimatePresence>
        {settingsToast && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 px-4 py-3 rounded-xl glass text-white text-sm z-50"
          >
            {settingsToast}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Kill Switch Confirmation Dialog */}
      <KillSwitchConfirmDialog />
      
      {/* Appearance Settings Modal */}
      <AppearanceModal />
    </div>
  )
}

// Sidebar Abilities - shows quick access after user starts chatting
interface SidebarAbilitiesProps {
  collapsed: boolean
  onAbilityClick: () => void
}

const QUICK_ABILITIES = [
  { icon: '🔍', name: 'Search', trigger: 'Search for...' },
  { icon: '🎥', name: 'Video', trigger: 'Create a video...' },
  { icon: '🚗', name: 'Uber', trigger: 'Get me an Uber...' },
  { icon: '📊', name: 'Chart', trigger: 'Create a chart...' },
  { icon: '▶️', name: 'YouTube', trigger: 'Play...' },
]

function SidebarAbilities({ collapsed, onAbilityClick }: SidebarAbilitiesProps) {
  const { messages } = useChatStore()

  // Only show after user has started chatting
  if (messages.length === 0) return null

  return (
    <div className="mt-4 pt-4 border-t border-white/10">
      <AnimatePresence>
        {!collapsed && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="px-5 text-xs font-medium text-white/40 uppercase tracking-wider mb-2"
          >
            Quick Actions
          </motion.p>
        )}
      </AnimatePresence>

      <div className={cn('flex flex-wrap gap-1', collapsed ? 'justify-center px-2' : 'px-4')}>
        {QUICK_ABILITIES.map((ability) => (
          <motion.button
            key={ability.name}
            onClick={onAbilityClick}
            className={cn(
              'rounded-lg transition-all hover:bg-emerald-500/20',
              collapsed
                ? 'w-10 h-10 flex items-center justify-center text-xl'
                : 'px-3 py-1.5 text-xs flex items-center gap-1.5 bg-white/5'
            )}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title={ability.name}
          >
            <span>{ability.icon}</span>
            {!collapsed && <span className="text-white/70">{ability.name}</span>}
          </motion.button>
        ))}
      </div>

      {!collapsed && (
        <button
          onClick={onAbilityClick}
          className="w-full mt-2 px-5 py-2 text-xs text-emerald-400 hover:text-emerald-300 hover:bg-white/5 transition-colors flex items-center justify-center gap-1"
        >
          <Sparkles className="w-3 h-3" />
          View all abilities
        </button>
      )}
    </div>
  )
}

interface NavItemProps {
  icon: typeof MessageSquare
  label: string
  isActive?: boolean
  collapsed?: boolean
  badge?: number
  onClick: () => void
}

function NavItem({ icon: Icon, label, isActive, collapsed, badge, onClick }: NavItemProps) {
  return (
    <motion.button
      onClick={onClick}
      whileHover={{ scale: 1.02, x: 4 }}
      whileTap={{ scale: 0.98 }}
      className={cn(
        'w-full flex items-center gap-4 px-5 py-4 mx-auto relative overflow-hidden',
        'text-white/80 hover:text-white transition-all duration-300',
        'hover:bg-emerald-500/10',
        isActive && 'text-emerald-400 bg-emerald-500/15 border-l-4 border-emerald-400'
      )}
    >
      {/* Hover glow effect */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-r from-emerald-500/0 via-emerald-500/10 to-emerald-500/0"
        initial={{ x: '-100%' }}
        whileHover={{ x: '100%' }}
        transition={{ duration: 0.6 }}
      />

      <div className="relative z-10">
        <div className={cn(
          'p-2 rounded-xl transition-all duration-300',
          isActive
            ? 'bg-gradient-to-br from-emerald-500/30 to-emerald-600/20 shadow-lg shadow-emerald-500/20'
            : 'bg-white/10 group-hover:bg-emerald-500/20'
        )}>
          <Icon className={cn(
            'w-6 h-6 transition-all duration-300',
            collapsed && 'mx-auto',
            isActive ? 'text-emerald-400' : 'text-white/90'
          )} />
        </div>
        {badge !== undefined && badge > 0 && (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-full text-[11px] font-bold flex items-center justify-center text-white shadow-lg shadow-emerald-500/40"
          >
            {badge}
          </motion.span>
        )}
      </div>
      <AnimatePresence>
        {!collapsed && (
          <motion.span
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: 'auto' }}
            exit={{ opacity: 0, width: 0 }}
            className={cn(
              'text-base font-semibold whitespace-nowrap overflow-hidden tracking-wide relative z-10',
              isActive ? 'text-emerald-400' : 'text-white/90'
            )}
          >
            {label}
          </motion.span>
        )}
      </AnimatePresence>
    </motion.button>
  )
}

// Abilities Page Component
interface AbilitiesPageProps {
  onTryAbility: (example: string) => void
}

const ABILITIES_DATA = [
  {
    id: 'web_search',
    name: 'Web Search',
    description: 'Search the internet for real-time information, news, and answers',
    icon: '🔍',
    examples: ["What's trending today?", 'Latest news about Kenya', 'Best restaurants near me'],
    category: 'Research',
    voice_triggers: ['search for', 'look up', 'find'],
  },
  {
    id: 'create_video',
    name: 'Create Videos',
    description: 'Generate professional promotional and social media videos',
    icon: '🎥',
    examples: ['Create a promo video for my business', 'Make a social media video', 'Generate a quote video'],
    category: 'Creative',
    voice_triggers: ['create video', 'make video', 'generate video'],
  },
  {
    id: 'uber_rides',
    name: 'Book Uber',
    description: 'Get Uber price estimates and book rides to your destination',
    icon: '🚗',
    examples: ['Get me an Uber to JKIA', 'How much is Uber from Westgate to Karen?'],
    category: 'Transport',
    voice_triggers: ['uber', 'get a ride', 'book taxi'],
  },
  {
    id: 'youtube',
    name: 'YouTube Videos',
    description: 'Find, play, and get transcripts from YouTube videos',
    icon: '▶️',
    examples: ['Play the latest Sauti Sol video', 'Find Python tutorials', 'Summarize this YouTube video'],
    category: 'Entertainment',
    voice_triggers: ['play video', 'youtube', 'watch'],
  },
  {
    id: 'trading',
    name: 'Trading Portfolio',
    description: 'View your forex trading performance and market analysis',
    icon: '📈',
    examples: ['Show my portfolio', "What's my current P/L?", 'Analyze EURUSD'],
    category: 'Finance',
    voice_triggers: ['my portfolio', 'trading', 'forex'],
  },
  {
    id: 'charts',
    name: 'Create Charts',
    description: 'Generate beautiful data visualizations and charts',
    icon: '📊',
    examples: ['Create a bar chart', 'Visualize this data', 'Make a pie chart'],
    category: 'Productivity',
    voice_triggers: ['create chart', 'make graph', 'visualize'],
  },
  {
    id: 'airtime',
    name: 'Buy Airtime',
    description: 'Purchase airtime for yourself or send to others',
    icon: '📱',
    examples: ['Buy me 100 bob airtime', 'Send 500 airtime to 0722000000'],
    category: 'Utilities',
    voice_triggers: ['buy airtime', 'top up', 'airtime'],
  },
  {
    id: 'email',
    name: 'Email Assistant',
    description: 'Read, search, and manage your connected email inbox',
    icon: '📧',
    examples: ['Check my inbox', 'Do I have any new emails?', 'Find emails from John'],
    category: 'Productivity',
    voice_triggers: ['check email', 'my emails', 'inbox'],
  },
  {
    id: 'movie_streaming',
    name: 'Find Movies',
    description: 'Find where to watch movies and TV shows online',
    icon: '🎬',
    examples: ['Where can I watch The Matrix?', 'Find free action movies', 'Stream Spider-Man'],
    category: 'Entertainment',
    voice_triggers: ['watch movie', 'find movie', 'stream'],
  },
  {
    id: 'image_analysis',
    name: 'Analyze Images',
    description: 'Upload and analyze images, documents, or photos',
    icon: '🖼️',
    examples: ['Analyze this image', 'What does this document say?', 'Read this receipt'],
    category: 'Productivity',
    voice_triggers: ['analyze image', 'look at this', 'read this'],
  },
  {
    id: 'music',
    name: 'Play Music',
    description: 'Find and play songs, albums, and playlists',
    icon: '🎵',
    examples: ['Play Afrobeats', 'Find Burna Boy songs', 'Play some workout music'],
    category: 'Entertainment',
    voice_triggers: ['play music', 'play song', 'music'],
  },
  {
    id: 'weather',
    name: 'Weather Updates',
    description: 'Get current weather and forecasts for any location',
    icon: '🌤️',
    examples: ["What's the weather in Nairobi?", 'Will it rain today?', 'Weather forecast this week'],
    category: 'Information',
    voice_triggers: ['weather', 'forecast', 'temperature'],
  },
]

const CATEGORIES = ['All', 'Research', 'Creative', 'Entertainment', 'Finance', 'Productivity', 'Transport', 'Utilities', 'Information']

function AbilitiesPage({ onTryAbility }: AbilitiesPageProps) {
  const [selectedCategory, setSelectedCategory] = useState('All')
  const [searchQuery, setSearchQuery] = useState('')

  const filteredAbilities = ABILITIES_DATA.filter((ability) => {
    const matchesCategory = selectedCategory === 'All' || ability.category === selectedCategory
    const matchesSearch =
      searchQuery === '' ||
      ability.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ability.description.toLowerCase().includes(searchQuery.toLowerCase())
    return matchesCategory && matchesSearch
  })

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <Zap className="w-8 h-8 text-emerald-400" />
            Niki's Abilities
          </h1>
          <p className="text-white/50">
            Discover what I can do for you - just ask or tap to try
          </p>
        </div>
        <div className="glass-card px-4 py-2 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-emerald-400" />
          <span className="text-sm text-white">{ABILITIES_DATA.length} abilities</span>
        </div>
      </div>

      {/* Search */}
      <div className="mb-6">
        <input
          type="text"
          placeholder="Search abilities..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="input"
        />
      </div>

      {/* Category filters */}
      <div className="flex flex-wrap gap-2 mb-6">
        {CATEGORIES.map((cat) => (
          <button
            key={cat}
            onClick={() => setSelectedCategory(cat)}
            className={cn(
              'px-4 py-2 rounded-lg text-sm font-medium transition-all',
              selectedCategory === cat
                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
            )}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Abilities grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredAbilities.map((ability, idx) => (
          <motion.div
            key={ability.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.05 }}
            className="glass-card p-4 hover:bg-white/10 transition-all cursor-pointer group"
            onClick={() => onTryAbility(ability.examples[0])}
          >
            <div className="flex items-start gap-3 mb-3">
              <span className="text-3xl">{ability.icon}</span>
              <div className="flex-1">
                <h3 className="font-semibold text-white">{ability.name}</h3>
                <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400">
                  {ability.category}
                </span>
              </div>
            </div>
            <p className="text-sm text-white/60 mb-3">{ability.description}</p>

            {/* Example chips */}
            <div className="flex flex-wrap gap-1.5">
              {ability.examples.slice(0, 2).map((ex, i) => (
                <button
                  key={i}
                  onClick={(e) => {
                    e.stopPropagation()
                    onTryAbility(ex)
                  }}
                  className="text-xs px-2 py-1 rounded-full bg-white/5 text-white/70 hover:bg-emerald-500/20 hover:text-emerald-300 transition-colors truncate max-w-[150px]"
                >
                  "{ex}"
                </button>
              ))}
            </div>

            {/* Voice trigger hint */}
            <div className="mt-3 pt-3 border-t border-white/5 opacity-0 group-hover:opacity-100 transition-opacity">
              <p className="text-xs text-emerald-400 flex items-center gap-1">
                <Mic className="w-3 h-3" />
                Say: "{ability.voice_triggers[0]}..."
              </p>
            </div>
          </motion.div>
        ))}
      </div>

      {filteredAbilities.length === 0 && (
        <div className="text-center py-12">
          <p className="text-white/50">No abilities match your search</p>
        </div>
      )}

      {/* Pro tip */}
      <div className="mt-8 glass-card p-4 bg-emerald-500/10 border border-emerald-500/20">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-emerald-400 mt-0.5" />
          <div>
            <h3 className="font-medium text-white mb-1">Pro Tip</h3>
            <p className="text-sm text-white/70">
              You don't need to remember exact commands! Just speak naturally - say things like
              "Get me an Uber to the airport" or "Create a video for my product launch" and I'll understand.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

// Settings Page Component
interface SettingsPageProps {
  user: { name: string; email: string; phone?: string; avatar_url?: string } | null
  onItemClick: (message: string) => void
}

function SettingsPage({ user, onItemClick }: SettingsPageProps) {
  const { setAppearanceModalOpen } = useUIStore()
  const [profileOpen, setProfileOpen] = useState(false)
  const [notificationsOpen, setNotificationsOpen] = useState(false)
  const [securityOpen, setSecurityOpen] = useState(false)
  const [voiceOpen, setVoiceOpen] = useState(false)
  const [pinOpen, setPinOpen] = useState(false)
  
  const handleItemClick = (label: string, action: string) => {
    switch (label) {
      case 'Appearance':
        setAppearanceModalOpen(true)
        break
      case 'Profile':
        setProfileOpen(true)
        break
      case 'Notifications':
        setNotificationsOpen(true)
        break
      case 'Security':
        setSecurityOpen(true)
        break
      case 'Niki PIN':
        setPinOpen(true)
        break
      case 'Voice & Sound':
      case 'Voice Input':
        setVoiceOpen(true)
        break
      case 'Help Center':
        window.open('https://help.nikiapp.com', '_blank')
        break
      default:
        onItemClick(action)
    }
  }
  
  const settingsSections = [
    {
      title: 'Account',
      items: [
        { icon: User, label: 'Profile', description: 'Manage your profile information', action: '' },
        { icon: Bell, label: 'Notifications', description: 'Configure notification preferences', action: '' },
        { icon: Shield, label: 'Security', description: 'Password and authentication', action: '' },
        { icon: Lock,
  History,
  ChevronRight, label: 'Niki PIN', description: 'Manage your Niki PIN for transactions', action: '' },
      ],
    },
    {
      title: 'Preferences',
      items: [
        { icon: Palette, label: 'Appearance', description: 'Theme and display settings', action: '' },
        { icon: Volume2, label: 'Voice & Sound', description: 'Audio and voice settings', action: '' },
        { icon: Mic, label: 'Voice Input', description: 'Configure voice recognition', action: '' },
      ],
    },
    {
      title: 'Support',
      items: [
        { icon: HelpCircle, label: 'Help Center', description: 'Get help and support', action: '' },
      ],
    },
  ]

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-3xl font-bold text-white mb-2">Settings</h1>
      <p className="text-white/50 mb-8">Manage your account and preferences</p>

      {/* Profile Card */}
      <div className="glass-card p-6 mb-8">
        <div className="flex items-center gap-4">
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold text-white"
            style={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' }}
          >
            {user?.name?.charAt(0).toUpperCase() || 'U'}
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-white">{user?.name || 'User'}</h2>
            <p className="text-white/50">{user?.email}</p>
          </div>
          <button
            className="btn-secondary"
            onClick={() => setProfileOpen(true)}
          >
            Edit Profile
          </button>
        </div>
      </div>

      {/* Settings Sections */}
      {settingsSections.map((section) => (
        <div key={section.title} className="mb-8">
          <h3 className="text-sm font-medium text-white/50 uppercase tracking-wider mb-4">
            {section.title}
          </h3>
          <div className="glass-card overflow-hidden divide-y divide-white/10">
            {section.items.map((item) => (
              <button
                key={item.label}
                onClick={() => handleItemClick(item.label, item.action)}
                className="w-full p-4 flex items-center gap-4 hover:bg-white/5 transition-colors text-left"
              >
                <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                  <item.icon className="w-5 h-5 text-emerald-400" />
                </div>
                <div className="flex-1">
                  <p className="text-white font-medium">{item.label}</p>
                  <p className="text-white/50 text-sm">{item.description}</p>
                </div>
                <ChevronLeft className="w-5 h-5 text-white/30 rotate-180" />
              </button>
            ))}
          </div>
        </div>
      ))}

      {/* Settings Modals */}
      <ProfileModal
        isOpen={profileOpen}
        onClose={() => setProfileOpen(false)}
        user={user}
        onSave={async () => {}}
      />
      <NotificationsModal
        isOpen={notificationsOpen}
        onClose={() => setNotificationsOpen(false)}
      />
      <SecurityModal
        isOpen={securityOpen}
        onClose={() => setSecurityOpen(false)}
      />
      <PINModal
        isOpen={pinOpen}
        onClose={() => setPinOpen(false)}
      />
      <VoiceModal
        isOpen={voiceOpen}
        onClose={() => setVoiceOpen(false)}
      />

      {/* Version Info */}
      <div className="text-center text-white/30 text-sm py-8">
        <p>Niki AI Digital Employee v1.0.0</p>
        <p className="mt-1">&copy; {new Date().getFullYear()} All rights reserved</p>
      </div>
    </div>
  )
}

// Tasks Page with tabs for Active Tasks and Scheduled Tasks
interface TasksPageContentProps {
  runningTasks: any[]
  
}

function TasksPageContent({ runningTasks }: TasksPageContentProps) {
  const [activeTab, setActiveTab] = useState<'active' | 'scheduled'>('active')

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Tasks</h1>
          <p className="text-white/50">Track active tasks and scheduled jobs</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="glass-card px-4 py-2 flex items-center gap-2">
            <Activity className="w-4 h-4 text-emerald-400" />
            <span className="text-sm text-white">{runningTasks.length} Active</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('active')}
          className={cn(
            'px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2',
            activeTab === 'active'
              ? 'bg-primary/20 text-primary-light'
              : 'text-white/60 hover:bg-white/5'
          )}
        >
          <Activity className="w-4 h-4" />
          Active Tasks
        </button>
        <button
          onClick={() => setActiveTab('scheduled')}
          className={cn(
            'px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2',
            activeTab === 'scheduled'
              ? 'bg-primary/20 text-primary-light'
              : 'text-white/60 hover:bg-white/5'
          )}
        >
          <ListTodo className="w-4 h-4" />
          Scheduled
        </button>
      </div>

      {/* Content */}
      {activeTab === 'active' ? (
        <div>
          {/* Info box */}
          <div className="mb-6 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-emerald-400 mt-0.5" />
              <div>
                <h3 className="font-medium text-white mb-1">Active Tasks</h3>
                <p className="text-sm text-white/70">
                  Real-time tracking of actions like sending money, scheduling payments, or executing trades.
                </p>
              </div>
            </div>
          </div>
          <TaskList showAll />
        </div>
      ) : (
        <ScheduledTasksPage />
      )}
    </div>
  )
}
